/**
 * Content for the app-download single-page landing (`lp/app/single`).
 *
 * Each drama carries 3 hook variants to A/B: the ad platform points one ad at each variant URL
 * (`?drama=<id>&v=<a|b|c>`), and we keep the best performer. `videoUrl` is optional — until the free
 * episode is scraped (WEB-105) the page renders the poster alone; dropping a URL in later turns the
 * hero into an autoplaying clip with no code change. Hooks are authored (ad-copy skill), ad-policy
 * safe, and kept inside the story.
 */
export interface DramaVariant {
  /** Short, stable id used in the URL (`?v=`) and the analytics/OneLink variant tag. */
  id: string;
  hook: string;
}

export interface LpDrama {
  dramaId: string;
  poster: string;
  /** Free-episode clip. Empty until scraped (WEB-105). */
  videoUrl?: string;
  rating?: number;
  viewCount?: number;
  variants: DramaVariant[];
}

export const CTA_LABEL = "Download to see what happens next";

export const DEFAULT_DRAMA_ID = "secret_of_the_rich_001";

export const lpDramas: Record<string, LpDrama> = {
  secret_of_the_rich_001: {
    dramaId: "secret_of_the_rich_001",
    poster: "https://asset.blidz.com/blidz-cdn/watch/short_dramas/secret-of-the-rich/poster.jpg",
    rating: 4.9,
    viewCount: 12840,
    variants: [
      {
        id: "a",
        hook: "He dumped the broke single mom for a richer bride — never knowing she was raising his daughter",
      },
      {
        id: "b",
        hook: "“Someone like you will never belong here.” They shoved a mop into her hands and laughed. Not one of them knew whose daughter the little girl was",
      },
      {
        id: "c",
        hook: "They made her scrub their floors like a servant. Then the little girl looked up at the billionaire and said one word: Daddy",
      },
    ],
  },
  "divorced-dominant-my-ex-in-jail-me-in-luxury": {
    dramaId: "divorced-dominant-my-ex-in-jail-me-in-luxury",
    poster:
      "https://asset.blidz.com/blidz-cdn/watch/short_dramas/divorced-dominant-my-ex-in-jail-me-in-luxury/poster.jpg",
    variants: [
      {
        id: "a",
        hook: "He handed her the divorce papers at her own birthday dinner — then pulled out the chair for her best friend. She hadn't even finished her cake",
      },
      {
        id: "b",
        hook: "“Take the fifty dollars and get out. You should be thanking me.” He had no idea who she was about to call",
      },
      {
        id: "c",
        hook: "The whole room laughed as he dragged his wife out by the arm. One man wasn't laughing. He was already dialing",
      },
    ],
  },
  "the-fallen-princess-blood-and-fire-over-the-capital": {
    dramaId: "the-fallen-princess-blood-and-fire-over-the-capital",
    poster:
      "https://asset.blidz.com/blidz-cdn/watch/short_dramas/the-fallen-princess-blood-and-fire-over-the-capital/poster.jpg",
    rating: 4.9,
    variants: [
      {
        id: "a",
        hook: "They spat on her and dragged her to the executioner for a murder she never committed. Then the sky turned black and the whole capital began to scream",
      },
      {
        id: "b",
        hook: "Her own stepmother poisoned the king and let her take the blame. The palace forgot one thing — the monster she'd saved as a bleeding child",
      },
      {
        id: "c",
        hook: "Stripped of her crown, disowned by her family, sentenced to burn at dawn. She had nothing left — except a promise made to a dragon",
      },
    ],
  },
  "the-last-anniversary": {
    dramaId: "the-last-anniversary",
    poster: "https://asset.blidz.com/blidz-cdn/watch/short_dramas/the-last-anniversary/poster.jpg",
    variants: [
      {
        id: "a",
        hook: "He served his wife divorce papers on their anniversary, in front of every friend they had. He never asked whose daughter he'd just shamed",
      },
      {
        id: "b",
        hook: "“You brought nothing to this marriage. Nothing.” He said it on their anniversary. Across town, her father put down the phone and stood up",
      },
      {
        id: "c",
        hook: "She walked in on him with another woman — and he screamed that it was her fault. By morning, his keycard wouldn't open his own office door",
      },
    ],
  },
  "blood-contract": {
    dramaId: "blood-contract",
    poster: "https://asset.blidz.com/blidz-cdn/watch/short_dramas/blood-contract/poster.jpg",
    rating: 4.7,
    variants: [
      {
        id: "a",
        hook: "To save her dying sister, she signed a contract with a man who hadn't aged a day in a hundred years",
      },
      {
        id: "b",
        hook: "“Sign here and your debt is gone.” She never read the last line — the one that handed him her blood",
      },
      {
        id: "c",
        hook: "They warned her the lord of the manor never stepped into daylight. By the time she understood why, she'd already said yes",
      },
    ],
  },
  "conflicted-hearts": {
    dramaId: "conflicted-hearts",
    poster: "https://asset.blidz.com/blidz-cdn/watch/short_dramas/conflicted-hearts/poster.jpg",
    variants: [
      {
        id: "a",
        hook: "She married the man who bought her family's company, and swore she would never fall for him",
      },
      {
        id: "b",
        hook: "On their wedding night he finally told her the truth — the marriage was revenge, and she was the weapon",
      },
      {
        id: "c",
        hook: "His family mocked the outsider he brought to dinner. Not one of them noticed the new will folded in his jacket",
      },
    ],
  },
  "mysterious-murderer": {
    dramaId: "mysterious-murderer",
    poster: "https://asset.blidz.com/blidz-cdn/watch/short_dramas/mysterious-murderer/poster.jpg",
    variants: [
      {
        id: "a",
        hook: "The night her father died, everyone at that dinner table had a reason to want him gone — and one of them acted on it",
      },
      {
        id: "b",
        hook: "The police called it an accident. Then she found her stepmother's name in her father's very last letter",
      },
      {
        id: "c",
        hook: "They all smiled for the family photo. One of them had already decided who wouldn't survive the weekend",
      },
    ],
  },
  "the-winter-veil": {
    dramaId: "the-winter-veil",
    poster: "https://asset.blidz.com/blidz-cdn/watch/short_dramas/the-winter-veil/poster.jpg",
    variants: [
      {
        id: "a",
        hook: "Her grandfather left the entire estate to her — so her cousins made sure she'd never live to claim it",
      },
      {
        id: "b",
        hook: "Snowed in at the family estate, she found the real will sewn into her dead mother's coat. Everything they'd told her was a lie",
      },
      {
        id: "c",
        hook: "They swore she was the poor relation, taken in out of pity. The letter buried in the snow said she owned all of it",
      },
    ],
  },
};
